import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { DEVICE_OVERVIEW_SECTIONS } from "../config/device-overview";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Grid,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
  Skeleton,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  TablePagination,
  Fab,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  PowerSettingsNew as RebootIcon,
  CameraAlt as ScreenshotIcon,
  Download as FetchLogsIcon,
  RestartAlt as FactoryResetIcon,
  PhoneAndroid as DeviceIcon,
  Wifi as NetworkIcon,
  Schedule as TimelineIcon,
  Code as CommandIcon,
  Description as LogsIcon,
  // Settings as SettingsIcon, // removed: Settings tab not shown
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Info as InfoIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  TouchApp as TouchAppIcon,
  NavigateNext as NavigateNextIcon,
  DevicesOther as DevicesOtherIcon,
  Sync as SyncIcon,
  Terminal as TerminalIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon,
  History as HistoryIcon,
  MonitorHeart as DashboardIcon,
  SystemUpdateAlt as SystemUpdateIcon,
  SwapHoriz as ChangeModeIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  useDevice,
  useDeviceCommands,
  useSendCommand,
  useDeleteDevice,
  useEnableActivityMonitor,
  useDisableActivityMonitor,
  useFactoryReset,
  useGetDeviceInfo,
  useReboot,
  useExecuteShellCommand,
} from "@/hooks/use-devices";
import {
  useDeviceFavorites,
  useToggleFavorite,
} from "@/hooks/use-device-favorites";
import { FavoriteStar } from "@/components/ui/favorite-star";
import { usePermissions } from "@/hooks/use-permissions";
import {
  DeviceStreamProvider,
  useSharedDeviceStream,
  useDeviceLiveLogging,
  useDeviceMemoryMonitor,
  useDeviceFeatureSessionEvent,
  useAnnounceFeatureStopped,
} from "@/contexts/device-stream-context";
import { LogViewer } from "@/components/log-viewer";
import { ActivityMonitor } from "@/components/activity-monitor";
import { MemoryMonitor } from "@/components/memory-monitor";
import { ShellCommandExecutor } from "@/components/shell-command-executor";
import { ScreenCapture } from "@/components/screen-capture";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CopyDeviceId } from "@/components/ui/copy-button";
import { InternalDeviceIcon } from "@/components/ui/internal-device-icon";
import { FeatureBadge } from "@/components/ui/demo-feature";
import { MergedLogsBrowser } from "@/components/merged-logs-browser";
import { DeviceAnalytics } from "@/components/device-analytics";
import { StorageExplorer } from "@/components/storage-explorer";
import { ActionAuditDetailDialog } from "@/components/action-audit-detail-dialog";
import { DeviceLiveDashboard } from "@/components/device-live-dashboard";
import { DeviceSoftwareUpdateTab } from "@/components/device-software-update-tab";
import { DeviceQuickLoginCard } from "@/components/device-quicklogin-card";
import { DeviceSoftwareVersions } from "@/components/device-software-versions";
import { FormattedTime } from "@/components/ui/formatted-time";
import { deviceService } from "@/lib/device.service";
import {
  displayDeviceTypeWithSubType,
  displayDeviceProtocol,
} from "@/lib/utils";
import { getDeviceStatusConfig } from "@/lib/status-config";
import { getDeviceDisplayLabel } from "@/lib/device-display";
import { formatBytes as sharedFormatBytes } from "@/lib/formatters";
import type {
  DeviceStatus,
  CommandType,
  LogLevel,
  DeviceActionAudit,
  DeviceActionAuditsResponse,
  DeviceLog,
} from "@/types";
import {
  ACTION_SHELL_EXECUTE,
  ACTION_DEVICE_REBOOT,
  ACTION_DEVICE_FACTORY_RESET,
  ACTION_DEVICE_DELETE,
  ACTION_SCREENSHOT_CAPTURE,
  ACTION_SCREENSHOT_DELETE,
  ACTION_LOG_FORCE_UPLOAD,
  ACTION_LOG_DOWNLOAD,
  ACTION_LOG_ARCHIVE_DOWNLOAD,
  ACTION_FILESYSTEM_LIST,
  ACTION_QUICKLOGIN_DELETE,
} from "@/types";

// Short display label per action. Storage / API keep the canonical
// dotted names ("shell.execute", "device.reboot"); this maps them to
// what the operator reads on screen. Unknown actions fall through to
// the raw canonical name so a new action category is at least legible.
function actionLabel(action: string): string {
  switch (action) {
    case ACTION_SHELL_EXECUTE:
      return "Shell";
    case ACTION_DEVICE_REBOOT:
      return "Reboot";
    case "device.get_info":
      return "Refresh Info";
    case "command.send":
      return "Send Command";
    case "command.cancel":
      return "Cancel Command";
    case "monitor.live_logging.enable":
      return "Enable Live Logging";
    case "monitor.live_logging.disable":
      return "Disable Live Logging";
    case "monitor.memory.enable":
      return "Enable Memory Monitor";
    case "monitor.memory.disable":
      return "Disable Memory Monitor";
    case "monitor.activity.enable":
      return "Enable Activity Monitor";
    case "monitor.activity.disable":
      return "Disable Activity Monitor";
    case "log.sync_gcs":
      return "Sync Logs (GCS)";
    case ACTION_DEVICE_FACTORY_RESET:
      return "Factory Reset";
    case ACTION_DEVICE_DELETE:
      return "Delete Device";
    case ACTION_SCREENSHOT_CAPTURE:
      return "Capture Screenshot";
    case ACTION_SCREENSHOT_DELETE:
      return "Delete Screenshot";
    case ACTION_LOG_FORCE_UPLOAD:
      return "Force Upload";
    case ACTION_LOG_DOWNLOAD:
      return "Download Log";
    case ACTION_LOG_ARCHIVE_DOWNLOAD:
      return "Download Archive";
    case ACTION_FILESYSTEM_LIST:
      return "List Filesystem";
    case ACTION_QUICKLOGIN_DELETE:
      return "Delete Quick Login backup";
    default:
      return action;
  }
}
import { useFeatures } from "@/hooks/use-features";
import { useAuthStore } from "@/stores/auth.store";
import { useUIStore } from "@/stores/ui.store";

// Disabled commands are hidden from the Commands panel
const DISABLED_COMMANDS = new Set([
  "lock",
  "unlock",
  "clear_cache",
  "wipe",
  "screenshot",
  "fetch_logs",
]);

// "Change Mode" quick action — switches which MDM backend environment a device
// talks to by setting the Android system property `persist.update.dev.mode`.
//
// NOTE: the `value` here is what gets written to the device and is NOT the same
// as the nominal config legend (config.yaml: 0=prod, 1=dev, 2=beta, 3=alpha):
//   - dev   -> 4  (deliberately NOT 1 — value 4 is the env our dev/local stack
//                  actually runs on; see config.dev.yaml `mode: "4"`, which is
//                  "reserved for local isolation". Devices must use 4 to reach it.)
//   - alpha -> 3  (matches config)
//   - beta  -> 2  (matches config)
//   - prod  -> 0  (matches config, but intentionally NOT selectable here so a
//                  test device can't be pointed at production by mistake)
//
// Stopgap: there is no dedicated change-mode command/interface yet, so we shell
// out via `setprop`. When the real interface lands, swap the body of
// `changeDeviceMode` below — the menu and this table stay as-is.
const DEVICE_MODES = [
  { key: "dev", label: "Dev", value: 4, selectable: true },
  { key: "alpha", label: "Alpha", value: 3, selectable: true },
  { key: "beta", label: "Beta", value: 2, selectable: true },
  { key: "prod", label: "Prod", value: 0, selectable: false },
] as const;
type DeviceMode = (typeof DEVICE_MODES)[number];

// The CRM is built/deployed per environment, so VITE_APP_ENV tells us which env
// a connected device is currently on — no need to query the device. Accept both
// our short keys ("dev"/"alpha"/"beta"/"prod") and the longer
// "development"/"production" aliases that build tooling sometimes injects.
// Unknown/unset → no "current" marker in the menu.
const ENV_ALIASES: Record<string, DeviceMode["key"]> = {
  dev: "dev",
  develop: "dev",
  development: "dev",
  alpha: "alpha",
  beta: "beta",
  prod: "prod",
  production: "prod",
};
const CURRENT_MODE = (() => {
  const raw = String(import.meta.env.VITE_APP_ENV ?? "").toLowerCase();
  return DEVICE_MODES.find((m) => m.key === (ENV_ALIASES[raw] ?? raw)) ?? null;
})();

// In-memory buffer is unbounded. The previous capped+IDB-paging design
// was meant to bound heap usage, but the actual OOM (React 19 dev
// profiler structured-cloning rowProps on every render) is fixed at the
// row level in log-viewer.tsx (logs array no longer goes through
// memoizedProps). Now the only ceiling is V8 heap — the heap-pressure
// banner below warns the user well before that's a problem.
const LIVE_LOG_BUFFER_CAP = Number.POSITIVE_INFINITY;

const LIVE_LOG_INFO_CAPTION =
  "Live logs are kept in your browser’s memory — long sessions with heavy logs may slow the page.";

const LIVE_LOG_INFO_TOOLTIP =
  "Today, every log line is kept in your browser’s memory while you’re on this page. This works well for normal sessions, but very long live sessions on a chatty device may slow the page down or trigger a memory warning at the top of this card.";

// Heap pressure thresholds. usedJSHeapSize / jsHeapSizeLimit ≥ this fires the
// warning banner. 0.7 leaves room to clear before Chrome's renderer crashes
// (which it does silently — there's no JS-catchable OOM error in V8).
const HEAP_PRESSURE_THRESHOLD = 0.7;
const HEAP_POLL_INTERVAL_MS = 3_000;

interface HeapInfo {
  used: number;
  limit: number;
  ratio: number;
}

// Reads performance.memory if the browser supports it (Chromium-only,
// non-standard). Returns null elsewhere so consumers can degrade gracefully
// — Firefox / Safari users just don't get the proactive warning.
function readHeapInfo(): HeapInfo | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mem = (performance as any).memory as
    | { usedJSHeapSize: number; jsHeapSizeLimit: number }
    | undefined;
  if (!mem || !mem.jsHeapSizeLimit) return null;
  return {
    used: mem.usedJSHeapSize,
    limit: mem.jsHeapSizeLimit,
    ratio: mem.usedJSHeapSize / mem.jsHeapSizeLimit,
  };
}

function formatMB(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

const ACTIVITY_MONITOR_NOTE =
  "Only foreground app/screen changes are tracked here (Android `onActivityChanged`). Individual taps, swipes, and in-app navigation within the same activity are not captured. The monitor auto-stops after 30 minutes; press Start Monitor again to resume.";

const commandMeta: Record<string, { label: string; icon: React.ReactNode }> = {
  lock: { label: "Lock", icon: <LockIcon /> },
  unlock: { label: "Unlock", icon: <UnlockIcon /> },
  reboot: { label: "Reboot", icon: <RebootIcon /> },
  screenshot: { label: "Screenshot", icon: <ScreenshotIcon /> },
  fetch_logs: { label: "Fetch Logs", icon: <FetchLogsIcon /> },
  factory_reset: { label: "Factory Reset", icon: <FactoryResetIcon /> },
  clear_cache: { label: "Clear Cache", icon: <RebootIcon /> },
  wipe: { label: "Wipe", icon: <FactoryResetIcon /> },
};

const formatBytes = (bytes?: number) =>
  sharedFormatBytes(bytes, { placeholder: "-", decimals: 1 });

function formatDate(date?: string): string {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 1.5 }}>{children}</Box>}
    </div>
  );
}

// The canonical tab slugs. Rendering order lives in the Tabs markup (overview
// first, action tabs in the middle, audit/history last so users reach the things
// they do more often without scrolling past the log); this only constrains which
// slugs a tab link may point at, so a typo fails to compile. tabMap below maps
// the same slugs — plus legacy aliases — back to tab indices.
type TabSlug =
  | "overview"
  | "software-update"
  | "diagnostics"
  | "analytics"
  | "screen-capture"
  | "shell"
  | "audit";

// Sub-tab slugs within Diagnostics.
type MonitoringSubTabSlug =
  | "live-logs"
  | "logs"
  | "activity"
  | "memory"
  | "storage";

function DeviceDetailBody() {
  const { id, tab, subtab } = useParams<{
    id: string;
    tab?: string;
    subtab?: string;
  }>();
  // Shared favorites cache — the same one the devices list and the sidebar read.
  const { machineIds: favoriteIds, canEdit: canStar } = useDeviceFavorites();
  const { toggle: toggleFavorite } = useToggleFavorite();
  const navigate = useNavigate();
  const location = useLocation();
  // Back button should return to the previous route the user came from
  // (Dashboard, Analytics, a different device, etc). `location.key` is
  // 'default' only for the very first entry of the session — in that
  // case there is nothing to pop, so fall back to the devices list.
  const handleBack = useCallback(() => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/devices");
    }
  }, [location.key, navigate]);
  const features = useFeatures();
  const { isSuperAdmin } = usePermissions();
  // Gate the Analytics tab on the backend-agnostic `analytics` flag, falling
  // back to the legacy `clickhouse` flag so a new frontend stays compatible
  // with an older backend that doesn't yet emit `features.analytics`.
  // Restricted to super admins — analytics is an internal/ops view.
  const analyticsEnabled =
    isSuperAdmin && (features.analytics ?? features.clickhouse);
  const auditEnabled =
    isSuperAdmin && (features.analytics ?? features.clickhouse);
  // Live Dashboard (Grafana-style fullscreen overlay). Hidden behind a
  // console feature flag so the entry point only appears when someone
  // with devtools access opts in — that's a stronger gate than role
  // alone (random viewers can't see it even if their account is admin).
  // Toggle via the existing nav console helper.
  const liveDashboardEnabled = useUIStore(
    (s) => !!s.hiddenNavEnabled["live-dashboard"],
  );
  const [dashboardOpen, setDashboardOpen] = useState(false);

  // Support old tab names for backwards-compatible URLs. Each sub-tab slug
  // (live-logs, logs, activity, memory, storage) also resolves to the parent
  // Diagnostics tab so URLs like /devices/{id}/activity land on the right pane.
  const tabMap: Record<string, number> = {
    overview: 0,
    "software-update": 1,
    // Back-compat: old slug + legacy URLs still resolve to the Software Update tab.
    "software-versions": 1,
    diagnostics: 2,
    monitoring: 2,
    "live-logs": 2,
    logs: 2,
    activity: 2,
    memory: 2,
    storage: 2,
    analytics: 3,
    "screen-capture": 4,
    screenshots: 4,
    shell: 5,
    audit: 6,
    commands: 6,
  };
  const activeTab = tabMap[tab || "overview"] ?? 0;

  // Sub-tab within Diagnostics: 0=Live Logs, 1=Logs, 2=Activity, 3=Memory, 4=Storage.
  // Canonical URL is nested — /devices/{id}/diagnostics/<sub> — so we look at
  // the `subtab` segment first, then fall back to `tab` for legacy flat URLs
  // (/devices/{id}/activity, /devices/{id}/memory, etc.) that predate nesting.
  const monitoringSubTabMap: Record<string, number> = {
    "live-logs": 0,
    logs: 1,
    activity: 2,
    memory: 3,
    storage: 4,
  };
  const monitoringSubTab = monitoringSubTabMap[subtab || tab || ""] ?? 0;

  // The tab bars are anchors now, so instead of navigate() handlers they need
  // hrefs. Typed against the canonical slug arrays: pass a slug that isn't in
  // the list and it fails to compile, which is what keeps the links and tabMap
  // from drifting apart.
  const tabHref = (slug: TabSlug) => `/devices/${id}/${slug}`;
  const subTabHref = (slug: MonitoringSubTabSlug) =>
    `/devices/${id}/diagnostics/${slug}`;
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [commandConfirmDialog, setCommandConfirmDialog] =
    useState<CommandType | null>(null);
  // Anchor for the super-admin "⋮" overflow menu in the top bar (change mode +
  // delete). null = closed.
  const [overflowAnchor, setOverflowAnchor] = useState<null | HTMLElement>(
    null,
  );
  // Mode-picker popup (lists the env options). Selecting one opens the
  // type-to-confirm dialog below.
  const [modePickerOpen, setModePickerOpen] = useState(false);
  // Mode pending confirmation (null = no confirm dialog open).
  const [modeConfirm, setModeConfirm] = useState<DeviceMode | null>(null);

  // monitoringSubTab: 0=Live Logs, 1=Log Files, 2=Activity, 3=Memory

  const [isLiveMode, setIsLiveMode] = useState(false);
  const liveLogLevel: LogLevel = "info";
  // When the user pauses live mode, logs emitted on the device during the
  // gap never reach the CRM (backend gates on set_logs=false). On resume
  // we inject a synthetic "pause marker" entry so the viewer shows a
  // visible gap at the right timestamp — otherwise Stop → Start looks
  // like a continuous stream and users miss that data was dropped.
  const pauseStartedAtRef = useRef<number | null>(null);
  const [pauseMarkers, setPauseMarkers] = useState<DeviceLog[]>([]);
  const [isActivityMode, setIsActivityMode] = useState(false);
  const [isMemoryMode, setIsMemoryMode] = useState(false);
  const [memoryThreshold] = useState(102400); // 100MB in KB
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null);
  const [infoSnackbar, setInfoSnackbar] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Dev-only console testing helpers. Attached to window.__mdm while the
  // device-detail page is mounted, so QC / devs can trigger each UI state
  // without needing a real device. `__mdm.help()` lists every command.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    interface MdmDev {
      errorSnackbar: (msg?: string) => void;
      infoSnackbar: (msg?: string) => void;
      rebootConfirm: () => void;
      factoryResetConfirm: () => void;
      deviceStatus: () => unknown;
      refreshDevice: () => void;
      watchHeap: (intervalMs?: number) => void;
      stopWatchHeap: () => void;
      help: () => void;
    }
    interface PerfMemory {
      usedJSHeapSize: number;
      jsHeapSizeLimit: number;
      totalJSHeapSize: number;
    }
    const w = window as unknown as {
      __mdm?: MdmDev;
      __mdmHeapTimer?: ReturnType<typeof setInterval>;
    };
    w.__mdm = {
      errorSnackbar: (msg = "Test error — device did not respond") =>
        setErrorSnackbar(msg),
      infoSnackbar: (msg = "Test info — reboot sent") => setInfoSnackbar(msg),
      rebootConfirm: () => setCommandConfirmDialog("reboot"),
      factoryResetConfirm: () => setCommandConfirmDialog("factory_reset"),
      deviceStatus: () => ({
        dbStatus: device?.status,
        displayStatus,
        lastOnline: device?.last_online,
        realtimeTimestamp: realtimeStatus?.timestamp,
        isStreamConnected,
      }),
      refreshDevice: () => {
        if (id) {
          queryClient.invalidateQueries({
            queryKey: ["devices", "detail", id],
          });
        }
      },
      watchHeap: (intervalMs = 1000) => {
        // Chromium-only — performance.memory is non-standard and absent
        // on Firefox/Safari. Bail out with a friendly note rather than
        // logging garbage.
        const mem = (performance as unknown as { memory?: PerfMemory }).memory;
        if (!mem) {
          console.warn(
            "[heap] performance.memory not available in this browser (Chromium-only)",
          );
          return;
        }
        if (w.__mdmHeapTimer) {
          clearInterval(w.__mdmHeapTimer);
        }
        w.__mdmHeapTimer = setInterval(() => {
          const m = (performance as unknown as { memory: PerfMemory }).memory;
          const used = (m.usedJSHeapSize / 1024 / 1024).toFixed(0);
          const limit = (m.jsHeapSizeLimit / 1024 / 1024).toFixed(0);
          const pct = ((m.usedJSHeapSize / m.jsHeapSizeLimit) * 100).toFixed(1);

          console.log(`heap: ${used}MB / ${limit}MB (${pct}%)`);
        }, intervalMs);

        console.log(
          `[heap] watching every ${intervalMs}ms — call __mdm.stopWatchHeap() to stop`,
        );
      },
      stopWatchHeap: () => {
        if (w.__mdmHeapTimer) {
          clearInterval(w.__mdmHeapTimer);
          delete w.__mdmHeapTimer;

          console.log("[heap] watch stopped");
        }
      },
      help: () => {
        console.table([
          { cmd: "__mdm.errorSnackbar(msg?)", desc: "Show red error snackbar" },
          { cmd: "__mdm.infoSnackbar(msg?)", desc: "Show blue info snackbar" },
          { cmd: "__mdm.rebootConfirm()", desc: "Open Reboot confirm dialog" },
          {
            cmd: "__mdm.factoryResetConfirm()",
            desc: "Open Factory Reset confirm dialog",
          },
          {
            cmd: "__mdm.deviceStatus()",
            desc: "Print current device status / staleness info",
          },
          {
            cmd: "__mdm.refreshDevice()",
            desc: "Invalidate this device detail cache",
          },
          {
            cmd: "__mdm.watchHeap(ms?)",
            desc: "Log JS heap usage every N ms (default 1000)",
          },
          { cmd: "__mdm.stopWatchHeap()", desc: "Stop the heap watcher" },
          { cmd: "__mdm.help()", desc: "Show this table" },
        ]);
      },
    };

    console.log(
      "[mdm-dev] Console helpers ready — call window.__mdm.help() to list commands.",
    );

    console.log(
      "[mdm-dev] Heap-watch snippet (copy/paste, no helpers needed):\n" +
        "setInterval(() => {\n" +
        "  const m = performance.memory;\n" +
        "  console.log(`heap: ${(m.usedJSHeapSize/1024/1024).toFixed(0)}MB / ${(m.jsHeapSizeLimit/1024/1024).toFixed(0)}MB (${(m.usedJSHeapSize/m.jsHeapSizeLimit*100).toFixed(1)}%)`);\n" +
        "}, 1000);",
    );
    return () => {
      if (w.__mdmHeapTimer) {
        clearInterval(w.__mdmHeapTimer);
        delete w.__mdmHeapTimer;
      }
      delete w.__mdm;
    };
    // We intentionally register once per mount; handlers close over latest
    // state via the component instance so re-registering on every state
    // change would be noisy in the console.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Extract a human-readable error message from an Axios-like error.
  const extractErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as {
      response?: {
        data?: { detail?: string; error?: string; message?: string };
      };
      message?: string;
    };
    return (
      e?.response?.data?.detail ||
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      fallback
    );
  };

  const { data: device, isLoading, error, refetch } = useDevice(id!);
  // Separate from isFetching so the Refresh button only spins when the user
  // clicked it — mount fetch + silent 30s polling stay invisible.
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  // `commands` data is no longer rendered (Device Command Queue tab
  // removed) but the hook stays so handleSendCommand can still
  // invalidate the cache after queueing — keeps future re-enable of
  // that surface cheap and reads from a warm query.
  const { refetch: refetchCommands } = useDeviceCommands(id!);

  // Shell audit list state. React Query so we get automatic refetch on
  // tab activation, polling while rows are pending, and `total` tracking
  // for pagination. See issue audit in docs: fixes "Pending forever"
  // display, missing stdout view, and stale data when navigating away
  // and back.
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(10);
  const [detailAudit, setDetailAudit] = useState<DeviceActionAudit | null>(
    null,
  );

  const {
    data: auditData,
    isFetching: auditsLoading,
    refetch: refetchAudits,
  } = useQuery<DeviceActionAuditsResponse>({
    queryKey: ["action-audits", id, auditRowsPerPage, auditPage],
    queryFn: () =>
      deviceService.listActionAudits(id!, {
        limit: auditRowsPerPage,
        offset: auditPage * auditRowsPerPage,
      }),
    enabled: !!id && activeTab === 6,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // Poll every 5s while any row on the current page is still
    // pending so the success/failure transition surfaces without a
    // manual refresh. Stops once every row reaches a terminal state.
    refetchInterval: (q) => {
      const d = q.state.data as DeviceActionAuditsResponse | undefined;
      if (!d?.audits?.length) return false;
      return d.audits.some((a) => a.result === "pending") ? 5000 : false;
    },
  });
  const actionAudits: DeviceActionAudit[] = auditData?.audits ?? [];
  const actionAuditsTotal = auditData?.total ?? actionAudits.length;

  // Single WebSocket for both status and logs (always connected for status)
  const {
    getLogs: getStreamedLogs,
    logsVersion,
    memoryReports,
    status: rawRealtimeStatus,
    isConnected: isStreamConnected,
    clearLogs: clearStreamedLogs,
    clearMemoryReports,
    reconnect: reconnectStream,
  } = useSharedDeviceStream({
    // The provider OR-aggregates wants across consumers, so passing the
    // page's own toggles is enough — when the dashboard wants logs too
    // they share one WS feed instead of opening a second socket.
    logs: isLiveMode || isActivityMode,
    memory: isMemoryMode,
    // Marks this tab as an activity watcher. Client-side only — it tells the
    // provider whether an activity session ending is worth a notification.
    activity: isActivityMode,
  });

  // Derived values pulled via the callback so neither this component nor
  // any child fiber ends up with the full log array in its memoizedProps
  // (which React 19's dev profiler structured-clones each render and
  // OOMs the tab past ~10k rows). Both memos depend on logsVersion so
  // they refresh on every buffer mutation.
  const streamedLogsCount = useMemo(
    () => getStreamedLogs().length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logsVersion, getStreamedLogs],
  );
  const activityLogs = useMemo(
    () => getStreamedLogs().filter((log) => log.log_type === "activity"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logsVersion, getStreamedLogs],
  );

  // Heap-pressure monitor. We can't catch a real OOM in JS — Chrome kills
  // the renderer with no exception — so the only honest mitigation is to
  // poll performance.memory (Chromium-only) and warn the user before they
  // hit the wall. heapInfo stays null on Firefox/Safari, which hides the
  // banner there; not ideal, but those users get the same crash they'd
  // get without any of this code, so we degrade silently rather than
  // pretend to monitor what we can't measure.
  const [heapInfo, setHeapInfo] = useState<HeapInfo | null>(null);
  useEffect(() => {
    const tick = () => setHeapInfo(readHeapInfo());
    tick();
    const id = setInterval(tick, HEAP_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  const heapWarning = heapInfo && heapInfo.ratio >= HEAP_PRESSURE_THRESHOLD;

  // After live logging is stopped, surface the captured-but-not-persisted log
  // count in the browser console only — the in-UI banner was removed to keep
  // the panel clean. logsVersion as the dep instead of array length so we
  // never have to touch the buffer itself outside getStreamedLogs().
  useEffect(() => {
    if (!isLiveMode) {
      const count = getStreamedLogs().length;
      if (count > 0) {
        console.log(
          `[live-logs] Showing ${count} captured live logs (not persisted)`,
        );
      }
    }
  }, [isLiveMode, logsVersion, getStreamedLogs]);

  // On page unload (F5, tab close, navigation) stop live logging on the device
  // immediately via sendBeacon — without this the backend only fires its 20s
  // debounced auto-disable, which means the device keeps streaming logs for
  // up to 20 seconds into a page that is already gone.
  useEffect(() => {
    if (!isLiveMode || !id) return;
    const handleBeforeUnload = () => {
      const apiBase = `${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/v1`;
      const url = `${apiBase}/devices/${id}/live-logging/disable?noWait=true`;
      const token =
        useAuthStore.getState().getAccessToken() ||
        useAuthStore.getState().accessToken;
      const body = new Blob([JSON.stringify({ reason: "page_unload" })], {
        type: "application/json",
      });
      // sendBeacon does not accept headers, so append token as query param if needed.
      // Our backend accepts Authorization via ?token= fallback; otherwise the
      // session cookie from the page covers it.
      const finalUrl = token
        ? `${url}&token=${encodeURIComponent(token)}`
        : url;
      navigator.sendBeacon(finalUrl, body);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isLiveMode, id]);

  // Cache last heartbeat so it survives brief WebSocket reconnects.
  // Expire after 60s (heartbeat timeout) so stale data doesn't persist.
  const cachedStatusRef = useRef(rawRealtimeStatus);
  if (rawRealtimeStatus) cachedStatusRef.current = rawRealtimeStatus;
  const [, forceRender] = useState(0);
  useEffect(() => {
    // Re-evaluate staleness every 15s so the UI transitions to "Waiting for heartbeat..."
    const timer = setInterval(() => forceRender((n) => n + 1), 15_000);
    return () => clearInterval(timer);
  }, []);
  const cached = cachedStatusRef.current;
  const isCacheStale = cached?.timestamp
    ? Date.now() - new Date(cached.timestamp).getTime() > 60_000
    : false;
  const realtimeStatus = rawRealtimeStatus ?? (isCacheStale ? null : cached);

  const sendCommand = useSendCommand(id!);
  const { data: allowedCommands } = useQuery({
    queryKey: ["allowed-commands", id],
    queryFn: () => deviceService.getAllowedCommands(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
  const deleteDevice = useDeleteDevice();
  // Activity monitor goes through direct mutations rather than the provider's
  // refcounted helpers: within a tab only this page consumes activity events.
  // Across tabs there is nothing to coordinate — Stop is global.
  const enableActivityMonitor = useEnableActivityMonitor(id!);
  const disableActivityMonitor = useDisableActivityMonitor(id!);

  // The device-side session can still end without this page asking — a TTL
  // lapse, the device going offline, a server restart. The provider raises the
  // notification; this drops the button out of its "monitoring" state so it
  // doesn't claim to be live in front of a stopped feed.
  const announceFeatureStopped = useAnnounceFeatureStopped();
  const featureSessionEvent = useDeviceFeatureSessionEvent();
  useEffect(() => {
    if (
      featureSessionEvent?.event === 'disabled' &&
      featureSessionEvent.feature_type === 'activity_monitor'
    ) {
      setIsActivityMode(false);
    }
  }, [featureSessionEvent]);
  // Live logging + memory monitor: refcounted via DeviceStreamProvider.
  // Calling the helper hook with `active=true` requests the device-side
  // stream from the provider; the provider invokes the actual enable
  // mutation on the 0→1 transition. Same for disable on 1→0. This is
  // what lets the dashboard and the page coexist without one tearing
  // down the other's stream.
  // Third argument: Stop is global, so when the device-global session ends —
  // another tab's Stop, a TTL lapse, the device going offline — this page has to
  // drop out of streaming state too, or the button keeps offering "Stop" for a
  // feed that has stopped. The provider raises the notification; this flips the
  // toggle the provider can't see.
  const { pending: liveLoggingPending } = useDeviceLiveLogging(
    isLiveMode,
    { level: liveLogLevel },
    () => setIsLiveMode(false),
  );
  useDeviceMemoryMonitor(
    isMemoryMode,
    { threshold: memoryThreshold },
    () => setIsMemoryMode(false),
  );
  const factoryReset = useFactoryReset(id!);
  const reboot = useReboot(id!);
  const getDeviceInfo = useGetDeviceInfo(id!);

  const handleSendCommand = async (type: CommandType) => {
    try {
      await sendCommand.mutateAsync({ type });
      refetchCommands();
      setCommandConfirmDialog(null);
    } catch {
      // Error handled by mutation
    }
  };

  // Backs the "Change Mode" quick action. See DEVICE_MODES above for why the
  // values don't line up 1:1 with the config legend (dev=4, not 1).
  const changeMode = useExecuteShellCommand(id!);

  // Swappable low-level "change mode" method. There is no dedicated change-mode
  // command/interface yet, so for now we set the system property over the shell
  // channel. When the real interface exists, replace ONLY this body — the menu
  // handler below stays the same.
  const changeDeviceMode = (value: number) =>
    changeMode.mutateAsync({
      command: `setprop persist.update.dev.mode ${value}`,
    });

  // Picker selection closes the picker and opens the type-to-confirm dialog —
  // the actual send happens on OK.
  const handleSelectMode = (mode: DeviceMode) => {
    setModePickerOpen(false);
    if (!mode.selectable) return; // prod is intentionally unclickable
    setModeConfirm(mode);
  };

  const confirmChangeMode = async () => {
    if (!modeConfirm) return;
    try {
      await changeDeviceMode(modeConfirm.value);
      setInfoSnackbar(
        `Mode set to ${modeConfirm.label} (persist.update.dev.mode=${modeConfirm.value}). The device will reconnect on the new environment.`,
      );
      setModeConfirm(null);
    } catch (err) {
      console.error("[change-mode] failed", err);
      setErrorSnackbar(
        extractErrorMessage(
          err,
          `Failed to change mode to ${modeConfirm.label}`,
        ),
      );
      setModeConfirm(null);
    }
  };

  const handleDelete = async () => {
    if (!device) return;
    try {
      await deleteDevice.mutateAsync(device.id);
      navigate("/devices");
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Breadcrumb skeleton */}
        <Skeleton variant="text" width={200} height={24} />
        {/* Header skeleton */}
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box>
                  <Skeleton variant="text" width={180} height={32} />
                  <Skeleton variant="text" width={120} height={20} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rounded" width={100} height={36} />
                <Skeleton variant="rounded" width={130} height={36} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {/* Tabs skeleton */}
        <Skeleton variant="rectangular" height={48} />
        {/* Content skeleton */}
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width={80} />
                  <Skeleton variant="text" width={120} height={32} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Failed to load device
        </Typography>
        <Button variant="contained" onClick={() => refetch()}>
          Try Again
        </Button>
      </Box>
    );
  }

  // Derive the displayed status from the realtime WS feed (matches the Real-time
  // Metrics card) so the header chip doesn't stay "Offline" while live metrics
  // are streaming in. Fall back to DB status only if no fresh WS event exists.
  //
  // Additional guard (mirrors devices.tsx): if BOTH the WS event is stale
  // (or absent) AND the DB last_online is older than the heartbeat timeout,
  // force-render "offline". Fixes the flicker QC reported where navigating
  // back into a device shows Online for a second (cached /devices/:id from
  // React Query) before the refetch corrects it — the cached status is only
  // trustworthy when last_online is fresh.
  const STALE_MS = 60_000;
  const now = Date.now();
  const realtimeTs = realtimeStatus?.timestamp
    ? new Date(realtimeStatus.timestamp).getTime()
    : 0;
  const isRealtimeFresh = !!realtimeTs && now - realtimeTs <= STALE_MS;
  const lastOnlineMs = device.last_online
    ? new Date(device.last_online).getTime()
    : 0;
  const lastOnlineStale = !lastOnlineMs || now - lastOnlineMs > STALE_MS;

  // The backend heartbeat-check goroutine can fire a presence=offline log with
  // a fire-time timestamp even when the device just re-heartbeated (race between
  // selecting stale rows and the heartbeat UPDATE landing). That offline event
  // arrives over WS within the 60s "fresh" window, so the naive "WS wins when
  // fresh" rule flips the chip to Offline despite last_online being fresher.
  // Guard: a WS offline claim older than last_online is stale — ignore it.
  const wsOfflineButDbFresher =
    isRealtimeFresh &&
    realtimeStatus?.status === "offline" &&
    lastOnlineMs > realtimeTs;
  const wsTrustworthy = isRealtimeFresh && !wsOfflineButDbFresher;

  let displayStatus: DeviceStatus;
  if (wsTrustworthy && realtimeStatus?.status) {
    displayStatus = realtimeStatus.status as DeviceStatus;
  } else if (lastOnlineStale) {
    displayStatus = "offline";
  } else {
    // last_online is fresh; if DB/cache still says offline (stale DB row, or
    // stale WS event propagated via setQueryData from /devices) override to
    // online. Preserves pending/blocked and other non-offline states.
    displayStatus = device.status === "offline" ? "online" : device.status;
  }
  const config = getDeviceStatusConfig(displayStatus);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        // Pull tighter than the global page padding on desktop — device
        // detail is already information-dense and the default 32px /
        // 24px wrappers feel loose against the sticky header and tabs.
        mx: { xs: -1, sm: -1.5, md: -2 },
      }}
    >
      {/* Sticky wrapper — keeps both the action row AND the tab strip
          pinned to the top while scrolling through device details, so
          the user never loses Refresh/Delete or the tab navigation. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: { xs: "wrap", md: "nowrap" },
            py: 1,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <IconButton onClick={handleBack} size="small" aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
            <Breadcrumbs
              separator={
                <NavigateNextIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
              }
            >
              <MuiLink
                component={Link}
                to="/devices"
                underline="hover"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { color: "#FF6720" },
                }}
              >
                <DevicesOtherIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Devices
              </MuiLink>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Typography
                  color="text.primary"
                  fontWeight={500}
                  sx={{ textTransform: "uppercase" }}
                >
                  {device.number ||
                    device.serial_number ||
                    device.machine_id ||
                    "—"}
                </Typography>
                <InternalDeviceIcon internal={device.is_internal} size={16} />
                <CopyDeviceId
                  machineId={device.machine_id}
                  fallback={device.number || device.serial_number}
                  size={16}
                />
                {/* Star sits with the identifier, not in the action bar: it
                    marks this machine rather than doing anything to it. */}
                <FavoriteStar
                  machineId={device.machine_id}
                  starred={
                    !!device.machine_id && favoriteIds.has(device.machine_id)
                  }
                  canStar={canStar}
                  meta={{
                    device_id: device.id,
                    number: device.number,
                    serial_number: device.serial_number,
                    status: device.status,
                  }}
                  onToggle={toggleFavorite}
                  size={18}
                />
              </Box>
            </Breadcrumbs>
            <Chip
              label={config.label}
              size="small"
              sx={{
                bgcolor: config.bg,
                color: config.color,
                fontWeight: 500,
                ml: 0.5,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {isSuperAdmin && (
            <Button
              variant="outlined"
              startIcon={
                isManualRefresh ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={async () => {
                setIsManualRefresh(true);
                try {
                  // Refresh every GET feeding the CURRENT tab in one click.
                  // All device-scoped query keys carry this device id (device
                  // object, software versions, screenshots, analytics, uptime,
                  // audits, log files…), so invalidating by id refetches the
                  // queries mounted on the visible tab now and marks the rest
                  // stale — they refresh when the operator switches to them.
                  await queryClient.invalidateQueries({
                    predicate: (q) => {
                      try {
                        return JSON.stringify(q.queryKey).includes(id!);
                      } catch {
                        return false;
                      }
                    },
                  });
                } finally {
                  setIsManualRefresh(false);
                }
              }}
              disabled={isManualRefresh}
              sx={{ borderColor: "divider", color: "text.primary" }}
            >
              Refresh
            </Button>
            )}
            {/* Super-admin overflow menu: change mode + delete. Both are
              privileged, low-frequency actions, so they live behind a "⋮"
              next to Refresh instead of taking up space in the bar. */}
            {isSuperAdmin && (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    aria-label="More actions"
                    onClick={(e) => setOverflowAnchor(e.currentTarget)}
                    sx={{
                      border: "1px solid #CCCCCC",
                      borderRadius: 1,
                      color: "#161616",
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={overflowAnchor}
                  open={!!overflowAnchor}
                  onClose={() => setOverflowAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {/* Change mode opens its own popup (the env picker); delete is a
                    separate destructive action. They're distinct features, so the
                    menu only lists entry points, not the mode options themselves.
                    The hover tooltip flags that switching env is a one-way action
                    from this CRM (or why it's disabled when the device is offline). */}
                  {(() => {
                    const modeOffline = displayStatus !== "online";
                    return (
                      <Tooltip
                        title={
                          modeOffline
                            ? "Device is offline. Change mode when it's back online."
                            : "Changing mode can't be rolled back from here. The device leaves this CRM and reconnects on the new environment."
                        }
                        placement="left"
                      >
                        {/* span wrapper so the tooltip still shows while the item is
                          disabled — a disabled MUI menu item swallows hover. */}
                        <Box component="span" sx={{ display: "block" }}>
                          <MenuItem
                            disabled={modeOffline || changeMode.isPending}
                            onClick={() => {
                              setOverflowAnchor(null);
                              setModePickerOpen(true);
                            }}
                            sx={{ px: 2 }}
                          >
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <ChangeModeIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Change mode" />
                          </MenuItem>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      setOverflowAnchor(null);
                      setDeleteDialog(true);
                    }}
                    sx={{ px: 2, color: "error.main" }}
                  >
                    <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete device…" />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          {/* Each Tab is a real anchor to its own URL, so right-click offers
              "Open in new tab" and ⌘/Ctrl/middle-click work. The tab routes
              already exist (/devices/:id/:tab), so a new tab lands on the same
              tab rather than bouncing to Overview.

              `replace` preserves today's behaviour — handleTabChange used
              navigate(..., {replace: true}), so switching tabs never stacked
              history entries and Back leaves the device rather than walking
              back through tabs. Tabs' own onChange is gone: with links the
              anchor does the navigating, and keeping both fired it twice. */}
          <Tabs
            value={activeTab}
            sx={{
              "& .MuiTab-root": { textTransform: "none" },
              "& .Mui-selected": { color: "#FF6720" },
              "& .MuiTabs-indicator": { bgcolor: "#FF6720" },
            }}
          >
            <Tab
              icon={<DeviceIcon />}
              iconPosition="start"
              label="Overview"
              component={Link}
              to={tabHref("overview")}
              replace
            />
            <Tab
              icon={<SystemUpdateIcon />}
              iconPosition="start"
              label="Software Update"
              component={Link}
              to={tabHref("software-update")}
              replace
            />
            <Tab
              icon={<SpeedIcon />}
              iconPosition="start"
              label="Diagnostics"
              component={Link}
              to={tabHref("diagnostics")}
              replace
            />
            <Tab
              icon={<AnalyticsIcon />}
              iconPosition="start"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  Analytics
                  <FeatureBadge label="Beta" tone="beta" />
                </Box>
              }
              component={Link}
              to={tabHref("analytics")}
              replace
              // Hidden via display:none (rather than removed) so the index of
              // subsequent tabs stays stable — TabPanel components match on
              // hard-coded numeric indices (0..5).
              sx={{ display: analyticsEnabled ? undefined : "none" }}
            />
            <Tab
              icon={<ScreenshotIcon />}
              iconPosition="start"
              label="Screen Capture"
              component={Link}
              to={tabHref("screen-capture")}
              replace
            />
            <Tab
              icon={<TerminalIcon />}
              iconPosition="start"
              label="Shell"
              component={Link}
              to={tabHref("shell")}
              replace
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="Audit"
              component={Link}
              to={tabHref("audit")}
              replace
              sx={{ display: auditEnabled ? undefined : "none" }}
            />
          </Tabs>
        </Box>
      </Box>
      {/* /sticky wrapper */}

      {/* Overview Tab */}
      <TabPanel value={activeTab} index={0}>
        {isLoading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width={80} />
                    <Skeleton variant="text" width={120} height={32} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Device Info */}
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DeviceIcon sx={{ color: "#666666", fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Device Info
                        </Typography>
                      </Box>
                    }
                    action={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={11}
                        >
                          Get latest info from device
                        </Typography>
                        <Tooltip
                          title={
                            device?.status !== "online"
                              ? "Device is offline"
                              : "Refresh will get new info directly from the device"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                device?.status !== "online" ||
                                getDeviceInfo.isPending
                              }
                              onClick={() => {
                                getDeviceInfo.mutate(undefined, {
                                  onSuccess: () => {
                                    // Command sent — device will respond via MQTT
                                    // Silently refetch device data after delay (only updates cache, no page flash)
                                    setTimeout(
                                      () => refetch({ cancelRefetch: false }),
                                      3000,
                                    );
                                    setTimeout(
                                      () => refetch({ cancelRefetch: false }),
                                      6000,
                                    );
                                  },
                                });
                              }}
                            >
                              {getDeviceInfo.isPending ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SyncIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ pt: 1.5 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Type
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "uppercase" }}
                        >
                          {displayDeviceTypeWithSubType(
                            device.type,
                            device.sub_type,
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Model
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "uppercase" }}
                        >
                          {device.model || "-"}
                        </Typography>
                      </Box>
                      {/* Protocol (DeviceInfo 1.6). The row is omitted entirely
                          on firmware that predates the field — an empty "-" here
                          would read as "this device has no controller" rather
                          than "this device is too old to say". A reported 0
                          ("Not detected") IS shown: a machine the agent looked
                          for and did not find is a symptom worth seeing. */}
                      {displayDeviceProtocol(device.device_protocol) && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Tooltip
                            title="How the machine controller is wired, reported by the device. UART is a machine controller on the fitness UART; USB HID is a USB rower whose endpoint the kiosk app owns."
                            placement="left"
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                borderBottom: "1px dotted",
                                borderColor: "text.disabled",
                                cursor: "help",
                              }}
                            >
                              Protocol
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {displayDeviceProtocol(device.device_protocol)}
                          </Typography>
                        </Box>
                      )}
                      {/* Agent/app versions live in the Software Versions card below. */}
                    </Box>
                  </CardContent>
                </Card>

                {/* Network */}
                {DEVICE_OVERVIEW_SECTIONS.network && (
                  <Card sx={{ height: "100%" }}>
                    <CardHeader
                      title={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <NetworkIcon
                            sx={{ color: "#666666", fontSize: 20 }}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            Network
                          </Typography>
                        </Box>
                      }
                      sx={{ pb: 0 }}
                    />
                    <CardContent sx={{ pt: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Public IP
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {device.public_ip || "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Connection
                          </Typography>
                          <Typography variant="body2">
                            {device.info?.network?.type || "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            WiFi SSID
                          </Typography>
                          <Typography variant="body2">
                            {device.info?.network?.ssid || "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Local IP
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {device.info?.network?.ip_address || "-"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            MAC
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {device.info?.mac_address || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Timeline */}
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TimelineIcon sx={{ color: "#666666", fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Timeline
                        </Typography>
                      </Box>
                    }
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ pt: 1.5 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Last Online
                        </Typography>
                        <Typography variant="body2" fontSize={12}>
                          {formatDate(device.last_online)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2" fontSize={12}>
                          {formatDate(device.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Storage */}
                {DEVICE_OVERVIEW_SECTIONS.storage && (
                  <Card sx={{ height: "100%" }}>
                    <CardHeader
                      title={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <StorageIcon
                            sx={{ color: "#666666", fontSize: 20 }}
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            Storage
                          </Typography>
                        </Box>
                      }
                      sx={{ pb: 0 }}
                    />
                    <CardContent>
                      <Box>
                        {device.info?.storage &&
                        device.info.storage.total_bytes > 0 ? (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LinearProgress
                                variant="determinate"
                                value={Math.round(
                                  (device.info.storage.used_bytes /
                                    device.info.storage.total_bytes) *
                                    100,
                                )}
                                sx={{
                                  flex: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#E6E6E6",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor: "#FF6720",
                                  },
                                }}
                              />
                              <Typography variant="body2">
                                {Math.round(
                                  (device.info.storage.used_bytes /
                                    device.info.storage.total_bytes) *
                                    100,
                                )}
                                %
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatBytes(device.info?.storage?.used_bytes)} /{" "}
                              {formatBytes(device.info?.storage?.total_bytes)} (
                              {formatBytes(device.info?.storage?.free_bytes)}{" "}
                              free)
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Real-time Metrics */}
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <SpeedIcon sx={{ color: "#666666", fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Real-time Metrics
                        </Typography>
                      </Box>
                    }
                    sx={{ pb: 0 }}
                  />
                  <CardContent>
                    {displayStatus !== "online" && displayStatus !== "away" ? (
                      <Box sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Device is offline
                        </Typography>
                      </Box>
                    ) : !realtimeStatus ||
                      (realtimeStatus.timestamp &&
                        (Date.now() -
                          new Date(realtimeStatus.timestamp).getTime()) /
                          1000 >
                          20) ? (
                      // >20s since the last heartbeat event — usually a reboot
                      // or network blip. Don't render stale CPU/mem as if live.
                      <Box sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Waiting for heartbeat…
                        </Typography>
                      </Box>
                    ) : realtimeStatus ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            CPU Usage
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={realtimeStatus.cpu_percent ?? 0}
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: "#E6E6E6",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor:
                                    (realtimeStatus.cpu_percent ?? 0) > 80
                                      ? "#E60A32"
                                      : (realtimeStatus.cpu_percent ?? 0) > 50
                                        ? "#F3A72E"
                                        : "#00BC3C",
                                },
                              }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 40 }}>
                              {realtimeStatus.cpu_percent != null
                                ? `${realtimeStatus.cpu_percent}%`
                                : "—"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Memory Usage
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <MemoryIcon
                              sx={{ color: "#666666", fontSize: 20 }}
                            />
                            <Typography variant="body2">
                              {realtimeStatus.memory_kb
                                ? `${(realtimeStatus.memory_kb / 1024).toFixed(1)} MB`
                                : "—"}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Last updated:{" "}
                          {new Date(
                            realtimeStatus.timestamp,
                          ).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {isStreamConnected
                            ? "Waiting for heartbeat..."
                            : "Connecting..."}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Login Backup */}
                {id && (
                  <DeviceQuickLoginCard
                    deviceId={id}
                    deviceLabel={getDeviceDisplayLabel(device).toUpperCase() || id}
                  />
                )}

                {/* Commands */}
                <Card>
                  <CardHeader
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CommandIcon sx={{ color: "#666666", fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Commands
                        </Typography>
                      </Box>
                    }
                    subheader="Commands are queued and executed when the device is online."
                    sx={{ pb: 0 }}
                  />
                  <CardContent>
                    {displayStatus !== "online" && (
                      <Alert
                        severity="warning"
                        variant="outlined"
                        sx={{ mb: 2, py: 0.25 }}
                      >
                        Device is offline — these commands need a live
                        connection and stay disabled until it reconnects.
                      </Alert>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {(allowedCommands || [])
                        .filter(
                          (c) =>
                            c.allowed &&
                            commandMeta[c.type] &&
                            !DISABLED_COMMANDS.has(c.type),
                        )
                        .map((cmd) => {
                          const meta = commandMeta[cmd.type];
                          // Use the staleness-aware displayStatus so the button
                          // reacts to heartbeat loss as fast as the header chip.
                          // Matches the confirm-dialog's reachability check below.
                          const isOffline = displayStatus !== "online";
                          const button = (
                            <Button
                              key={cmd.type}
                              variant={cmd.danger ? "contained" : "outlined"}
                              startIcon={meta.icon}
                              onClick={() =>
                                cmd.danger
                                  ? setCommandConfirmDialog(
                                      cmd.type as CommandType,
                                    )
                                  : handleSendCommand(cmd.type as CommandType)
                              }
                              disabled={sendCommand.isPending || isOffline}
                              sx={
                                cmd.danger
                                  ? {
                                      bgcolor: "#FF6720",
                                      "&:hover": { bgcolor: "#F5490F" },
                                    }
                                  : { borderColor: "#CCCCCC", color: "#161616" }
                              }
                            >
                              {meta.label}
                            </Button>
                          );
                          return isOffline ? (
                            <Tooltip key={cmd.type} title="Device is offline">
                              <span>{button}</span>
                            </Tooltip>
                          ) : (
                            button
                          );
                        })}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Right column — software inventory */}
            <Grid size={{ xs: 12, md: 6 }}>
              <DeviceSoftwareVersions deviceId={id!} />
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Software Update Tab */}
      <TabPanel value={activeTab} index={1}>
        {id && <DeviceSoftwareUpdateTab deviceId={id} />}
      </TabPanel>

      {/* Audit Tab — last because it's a reference view; action tabs are above */}
      <TabPanel value={activeTab} index={6}>
        {auditsLoading && (
          <LinearProgress
            sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#FF6720" } }}
          />
        )}

        {/* Unified action audits — single timeline for every operator action.
            The legacy "Device Commands" sub-tab was removed because the
            same logical command (reboot, factory-reset) flowed through
            two parallel paths and showed up in only one of the surfaces
            depending on which UI button was clicked. Action History is
            now the canonical record; delivery state for queued commands
            still lives in the commands table but isn't surfaced here. */}
        <Card>
          <CardHeader
            title="Action History"
            subheader="Operator actions against this device — shell commands, reboots, factory resets, screenshots, log downloads, filesystem listings."
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetchAudits()}
                disabled={auditsLoading}
              >
                Refresh
              </Button>
            }
          />
          <CardContent>
            {actionAudits.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <TerminalIcon sx={{ fontSize: 48, color: "#CCC", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No actions recorded
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Operator actions against this device will appear here.
                </Typography>
              </Box>
            ) : (
              <>
                <List disablePadding>
                  {actionAudits.map((audit, index) => {
                    // Status chip is driven by the unified result enum.
                    // Pending rows are the only ones the poll loop cares
                    // about; failed rows surface the audit.error inline.
                    let chip: { label: string; color: string; bg: string };
                    if (audit.result === "pending") {
                      chip = {
                        label: "Pending",
                        color: "#F3A72E",
                        bg: "#FEF6E9",
                      };
                    } else if (audit.result === "failure") {
                      // For shell rows, prefer the precise exit code
                      // over the generic "Failed" label — same legibility
                      // as the old shell-only table.
                      const exitCode =
                        audit.action === ACTION_SHELL_EXECUTE
                          ? (audit.result_data?.exit_code as number | undefined)
                          : undefined;
                      chip = {
                        label:
                          typeof exitCode === "number"
                            ? `Exit ${exitCode}`
                            : "Failed",
                        color: "#E60A32",
                        bg: "#FDE7EB",
                      };
                    } else {
                      chip = {
                        label: "Success",
                        color: "#00BC3C",
                        bg: "#E6F8EC",
                      };
                    }
                    return (
                      <ListItem
                        key={audit.id}
                        divider={index < actionAudits.length - 1}
                        onClick={() => setDetailAudit(audit)}
                        sx={{
                          px: 1,
                          alignItems: "flex-start",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Chip
                                label={actionLabel(audit.action)}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  bgcolor: "#EEF2FF",
                                  color: "#3949AB",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }} />
                              <Chip
                                label={chip.label}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  bgcolor: chip.bg,
                                  color: chip.color,
                                  flexShrink: 0,
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                mt: 0.5,
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <FormattedTime
                                value={audit.executed_at}
                                variant="auto"
                                sx={{ fontSize: 11, color: "text.secondary" }}
                              />
                              {audit.executed_by && (
                                <Tooltip
                                  title={
                                    audit.executed_by_email || audit.executed_by
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: 10,
                                      cursor: "help",
                                    }}
                                  >
                                    by{" "}
                                    {audit.executed_by.length > 12
                                      ? `${audit.executed_by.slice(0, 8)}…`
                                      : audit.executed_by}
                                  </Typography>
                                </Tooltip>
                              )}
                              {audit.error && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: 10,
                                    color: "#E60A32",
                                  }}
                                >
                                  {audit.error.length > 80
                                    ? `${audit.error.slice(0, 80)}…`
                                    : audit.error}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
                <TablePagination
                  component="div"
                  count={actionAuditsTotal}
                  page={auditPage}
                  onPageChange={(_, newPage) => setAuditPage(newPage)}
                  rowsPerPage={auditRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setAuditRowsPerPage(parseInt(e.target.value, 10));
                    setAuditPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Showing ${from}-${to} of ${count.toLocaleString()}`
                  }
                  sx={{ borderTop: 0 }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Logs Tab */}
      {/* Monitoring Tab (Logs + Activity + Memory) */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 1 }}>
          {/* Anchors, like the tab bar above: each sub-tab has its own route
              (/devices/:id/diagnostics/:subtab), so right-click → "Open in new
              tab" lands on that sub-tab. `replace` keeps the existing
              no-history-entry behaviour from handleMonitoringSubTabChange, and
              onChange is dropped so the anchor is the only thing navigating. */}
          <ToggleButtonGroup
            value={monitoringSubTab}
            exclusive
            size="small"
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                borderRadius: "6px !important",
                mx: 0.5,
                my: 0.5,
                px: 2,
                py: 0.75,
                textTransform: "none",
                fontSize: 13,
                fontWeight: 500,
                color: "text.secondary",
                gap: 0.75,
                "&.Mui-selected": {
                  bgcolor: "#FF6720",
                  color: "#fff",
                  "&:hover": { bgcolor: "#F5490F" },
                },
                "&:hover": { bgcolor: "action.hover" },
              },
            }}
          >
            <ToggleButton
              value={0}
              component={Link}
              to={subTabHref("live-logs")}
              replace
            >
              <LogsIcon sx={{ fontSize: 18 }} /> Live Logs
            </ToggleButton>
            <ToggleButton
              value={1}
              component={Link}
              to={subTabHref("logs")}
              replace
            >
              <LogsIcon sx={{ fontSize: 18 }} /> Logs
            </ToggleButton>
            <ToggleButton
              value={2}
              component={Link}
              to={subTabHref("activity")}
              replace
            >
              <TouchAppIcon sx={{ fontSize: 18 }} /> Activity
            </ToggleButton>
            <ToggleButton
              value={3}
              component={Link}
              to={subTabHref("memory")}
              replace
            >
              <MemoryIcon sx={{ fontSize: 18 }} /> Memory
            </ToggleButton>
            <ToggleButton
              value={4}
              component={Link}
              to={subTabHref("storage")}
              replace
            >
              <StorageIcon sx={{ fontSize: 18 }} /> Storage
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Logs sub-tab — unified DB-tracked log_files + GCS-scanned archive */}
        {monitoringSubTab === 1 && (
          <Card>
            <CardContent>
              <MergedLogsBrowser
                deviceId={id!}
                deviceStatus={device?.status || "offline"}
              />
            </CardContent>
          </Card>
        )}

        {/* Live Logs sub-tab */}
        {monitoringSubTab === 0 && (
          <Card>
            <CardHeader
              title={
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LogsIcon />
                    <Typography variant="h6">Device Logs</Typography>
                  </Box>
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          whiteSpace: "pre-line",
                          fontSize: 12,
                          lineHeight: 1.5,
                          maxWidth: 360,
                        }}
                      >
                        {LIVE_LOG_INFO_TOOLTIP}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        cursor: "help",
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 14, color: "#9E9E9E" }} />
                      <Typography variant="caption" color="text.secondary">
                        {LIVE_LOG_INFO_CAPTION}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              }
              action={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip
                    title={
                      device?.status !== "online" && !isLiveMode
                        ? "Device is offline"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant={isLiveMode ? "contained" : "outlined"}
                        startIcon={isLiveMode ? <PauseIcon /> : <PlayIcon />}
                        // Disable while a previous enable/disable is in flight so
                        // spam-clicking can't fire a storm of toggles at the device.
                        disabled={
                          liveLoggingPending ||
                          (device?.status !== "online" && !isLiveMode)
                        }
                        onClick={() => {
                          if (liveLoggingPending) return;
                          if (isLiveMode) {
                            // Stop live logging - disconnect WebSocket first, then notify device.
                            // Record pause start so the next Start can stamp a gap marker.
                            pauseStartedAtRef.current = Date.now();
                            setIsLiveMode(false);
                            // useDeviceLiveLogging effect calls disable on the
                            // 1→0 transition (when no other consumer wants it).
                          } else {
                            // Start live logging - connect WebSocket immediately, then notify device.
                            // If a previous pause is pending, inject a synthetic log entry so the
                            // viewer shows "logs emitted during this window are missing" at the
                            // correct chronological position.
                            if (pauseStartedAtRef.current && id) {
                              const startedAt = pauseStartedAtRef.current;
                              const endedAt = Date.now();
                              const ms = Math.max(0, endedAt - startedAt);
                              // Human-readable duration (s / m s / h m).
                              const s = Math.round(ms / 1000);
                              const durStr =
                                s < 60
                                  ? `${s}s`
                                  : s < 3600
                                    ? `${Math.floor(s / 60)}m ${s % 60}s`
                                    : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
                              setPauseMarkers((prev) => [
                                ...prev,
                                {
                                  id: `pause-${startedAt}`,
                                  device_id: id,
                                  log_type: "system",
                                  level: "warning",
                                  // Timestamp the marker at the MIDPOINT of the gap so
                                  // it sorts between the last captured log (at startedAt)
                                  // and the first new log (arriving after endedAt).
                                  created_at: new Date(
                                    startedAt + Math.floor(ms / 2),
                                  ).toISOString(),
                                  message: `⏸ Live logging paused for ${durStr} — any logs emitted by the device during this window were not captured.`,
                                  metadata: {
                                    kind: "pause_gap",
                                    paused_ms: ms,
                                    started_at: startedAt,
                                    ended_at: endedAt,
                                  },
                                },
                              ]);
                              pauseStartedAtRef.current = null;
                            }
                            setIsLiveMode(true);
                            // useDeviceLiveLogging effect calls enable on the
                            // 0→1 transition (provider refcount).
                          }
                        }}
                        sx={
                          isLiveMode
                            ? {
                                bgcolor: "#FF6720",
                                "&:hover": { bgcolor: "#F5490F" },
                              }
                            : { borderColor: "#CCCCCC", color: "#161616" }
                        }
                      >
                        {isLiveMode ? "Stop Live" : "Start Live"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              }
            />
            <CardContent>
              {heapWarning && heapInfo && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          clearStreamedLogs();
                          setPauseMarkers([]);
                          pauseStartedAtRef.current = null;
                        }}
                      >
                        Clear logs
                      </Button>
                      {isLiveMode && (
                        <Button
                          size="small"
                          onClick={() => {
                            pauseStartedAtRef.current = Date.now();
                            setIsLiveMode(false);
                          }}
                        >
                          Stop Live
                        </Button>
                      )}
                    </Box>
                  }
                >
                  Memory pressure is high — using {formatMB(heapInfo.used)} of{" "}
                  {formatMB(heapInfo.limit)} ({Math.round(heapInfo.ratio * 100)}
                  %). The browser tab may crash if it keeps climbing. Clear the
                  live buffer or stop streaming to free memory.
                </Alert>
              )}
              {/* Display priority:
                1. Any streamed logs we've captured (live or already-stopped) —
                   stopping live shouldn't erase the lines the user was just
                   reading. isLive={isLiveMode} controls LogViewer autoscroll
                   and the "N new logs" pill; the logs themselves stay.
                2. "Waiting/Connecting" placeholder while live is on but
                   nothing has arrived yet.
                3. Persisted DB logs when live is off and nothing has been
                   captured this session. */}
              {streamedLogsCount > 0 || pauseMarkers.length > 0 ? (
                <Box>
                  <LogViewer
                    getLogs={getStreamedLogs}
                    logsVersion={logsVersion}
                    extraLogs={pauseMarkers}
                    height={500}
                    onClear={() => {
                      clearStreamedLogs();
                      setPauseMarkers([]);
                      pauseStartedAtRef.current = null;
                    }}
                    isLive={isLiveMode}
                    hideLevelFilter
                    hideTypeFilter
                  />
                </Box>
              ) : isLiveMode ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <PlayIcon sx={{ fontSize: 48, color: "#CCCCCC", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {isStreamConnected
                      ? "Waiting for logs..."
                      : "Connecting..."}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Logs will appear here in real-time
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <LogsIcon sx={{ fontSize: 48, color: "#CCCCCC", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No live logs yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click <strong>Start Live</strong> to stream logs from the
                    device. Historical logs live under the Logs sub-tab.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        {/* Activity sub-tab */}
        {monitoringSubTab === 2 && (
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TouchAppIcon />
                  <Typography variant="h6">Activity Monitor</Typography>
                </Box>
              }
              action={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip
                    title={
                      device?.status !== "online" && !isActivityMode
                        ? "Device is offline"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant={isActivityMode ? "contained" : "outlined"}
                        startIcon={
                          isActivityMode ? <PauseIcon /> : <PlayIcon />
                        }
                        disabled={
                          device?.status !== "online" && !isActivityMode
                        }
                        onClick={() => {
                          if (isActivityMode) {
                            setIsActivityMode(false);
                            disableActivityMonitor.mutate();
                            // Activity bypasses the provider's refcount, so it
                            // announces its own stop to the other tabs.
                            announceFeatureStopped("activity_monitor");
                          } else {
                            setIsActivityMode(true);
                            enableActivityMonitor.mutate();
                          }
                        }}
                        sx={
                          isActivityMode
                            ? {
                                bgcolor: "#FF6720",
                                "&:hover": { bgcolor: "#F5490F" },
                              }
                            : { borderColor: "#CCCCCC", color: "#161616" }
                        }
                      >
                        {isActivityMode ? "Stop Monitor" : "Start Monitor"}
                      </Button>
                    </span>
                  </Tooltip>
                  {isActivityMode && !isStreamConnected && (
                    <IconButton onClick={reconnectStream}>
                      <RefreshIcon />
                    </IconButton>
                  )}
                </Box>
              }
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                {ACTIVITY_MONITOR_NOTE}
              </Alert>
              <ActivityMonitor
                activities={activityLogs}
                isLive={isActivityMode}
                onClear={clearStreamedLogs}
                height={500}
              />
            </CardContent>
          </Card>
        )}

        {/* Memory sub-tab */}
        {monitoringSubTab === 3 && (
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MemoryIcon />
                  <Typography variant="h6">Memory Monitor</Typography>
                </Box>
              }
              action={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip
                    title={
                      device?.status !== "online"
                        ? "Device is offline"
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant={isMemoryMode ? "contained" : "outlined"}
                        startIcon={isMemoryMode ? <PauseIcon /> : <PlayIcon />}
                        disabled={device?.status !== "online"}
                        onClick={() => {
                          // useDeviceMemoryMonitor effect handles enable/disable
                          // on the 0↔1 transition via the provider refcount.
                          setIsMemoryMode((prev) => !prev);
                        }}
                        sx={
                          isMemoryMode
                            ? {
                                bgcolor: "#FF6720",
                                "&:hover": { bgcolor: "#F5490F" },
                              }
                            : { borderColor: "#CCCCCC", color: "#161616" }
                        }
                      >
                        {isMemoryMode ? "Stop Monitor" : "Start Monitor"}
                      </Button>
                    </span>
                  </Tooltip>
                  {isMemoryMode && !isStreamConnected && (
                    <IconButton onClick={reconnectStream}>
                      <RefreshIcon />
                    </IconButton>
                  )}
                </Box>
              }
            />
            <CardContent>
              <MemoryMonitor
                memoryReports={memoryReports}
                threshold={memoryThreshold}
                isLive={isMemoryMode}
                onClear={clearMemoryReports}
                height={400}
              />
            </CardContent>
          </Card>
        )}

        {/* Storage Explorer sub-tab */}
        {monitoringSubTab === 4 && (
          <StorageExplorer
            deviceId={id!}
            isOnline={device?.status === "online"}
          />
        )}
      </TabPanel>

      {/* Analytics Tab */}
      <TabPanel value={activeTab} index={3}>
        {analyticsEnabled ? (
          <DeviceAnalytics deviceId={id!} lastOnline={device?.last_online} />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 360,
              py: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 2,
                maxWidth: 480,
                p: 5,
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                bgcolor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AnalyticsIcon sx={{ fontSize: 26, color: "#2563EB" }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#111111" }}
              >
                Analytics is not enabled
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable device metrics, uptime, and events by configuring an
                analytics backend.
              </Typography>
              <Box
                component="code"
                sx={{
                  bgcolor: "#F4F4F4",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: "#374151",
                }}
              >
                APP_ANALYTICS_BACKEND = clickhouse | postgres
              </Box>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Screen Capture Tab */}
      <TabPanel value={activeTab} index={4}>
        <ScreenCapture deviceId={id!} isOnline={device?.status === "online"} />
      </TabPanel>

      {/* Shell Tab */}
      <TabPanel value={activeTab} index={5}>
        <ShellCommandExecutor
          deviceId={id!}
          isOnline={device?.status === "online"}
        />
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Device"
        description={`Are you sure you want to delete "${getDeviceDisplayLabel(device).toUpperCase()}"? This action cannot be undone.`}
        confirmLabel="Delete"
        loadingLabel="Deleting…"
        destructive
        loading={deleteDevice.isPending}
      />

      {/* Shell Audit Detail Dialog — shown when a row is clicked */}
      <ActionAuditDetailDialog
        audit={detailAudit}
        onClose={() => setDetailAudit(null)}
      />

      {/* Floating trigger for the Live Dashboard. Rendered at the page
          root so it stays pinned across all tabs. Hidden by default —
          gated solely on the console feature flag (intentionally
          undocumented). */}
      {liveDashboardEnabled && (
        <Tooltip title="Open Live Dashboard" placement="left">
          <Fab
            color="primary"
            onClick={() => setDashboardOpen(true)}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              bgcolor: "#FF6720",
              "&:hover": { bgcolor: "#F5490F" },
              zIndex: (t) => t.zIndex.speedDial,
            }}
          >
            <DashboardIcon />
          </Fab>
        </Tooltip>
      )}
      <DeviceLiveDashboard
        open={dashboardOpen}
        onClose={() => setDashboardOpen(false)}
        device={device ?? null}
      />

      {/* Command Confirmation Dialog */}
      <Dialog
        open={!!commandConfirmDialog}
        onClose={() => setCommandConfirmDialog(null)}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to execute "
            {commandConfirmDialog?.replace("_", " ")}"?
            {commandConfirmDialog !== "reboot" &&
              " This action cannot be undone."}
            {(commandConfirmDialog === "factory_reset" ||
              commandConfirmDialog === "reboot") &&
              device?.status === "online" && (
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "#FF6720",
                    fontWeight: 500,
                  }}
                >
                  Device is online - will execute immediately via MDM Protocol.
                </Box>
              )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommandConfirmDialog(null)}
            sx={{ color: "#161616" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Use displayStatus (60s-staleness-aware derivation from WS
              // heartbeat) instead of the DB-only `device.status`. Closes
              // the window where the UI still shows online right after a
              // network drop (see FR-0315).
              const reachable = displayStatus === "online";
              if (commandConfirmDialog === "reboot" && reachable) {
                // Fire-and-forget: backend defaults to noWait for reboot
                // because a rebooting device tears down MQTT before it can
                // reply. We show an info toast immediately and let the
                // heartbeat-staleness check flip the status chip when the
                // device actually goes down.
                reboot.mutate(
                  { delayMs: 0 },
                  {
                    onSuccess: () => {
                      setInfoSnackbar(
                        "Reboot sent — device will restart shortly.",
                      );
                      setCommandConfirmDialog(null);
                      // Nudge a refetch so the status chip updates as soon as
                      // the backend flips the DB after the heartbeat timeout.
                      queryClient.invalidateQueries({
                        queryKey: ["devices", id],
                      });
                    },
                    onError: (err) => {
                      console.error("[reboot] failed", err);
                      setErrorSnackbar(
                        extractErrorMessage(
                          err,
                          "Reboot failed — could not publish to MQTT",
                        ),
                      );
                      setCommandConfirmDialog(null);
                    },
                  },
                );
              } else if (commandConfirmDialog === "reboot") {
                // Device offline — don't silently queue via the generic command endpoint.
                setErrorSnackbar("Device is not online — cannot send reboot.");
                setCommandConfirmDialog(null);
              } else if (
                commandConfirmDialog === "factory_reset" &&
                reachable
              ) {
                factoryReset.mutate(true, {
                  onSuccess: () => setCommandConfirmDialog(null),
                  onError: (err) => {
                    console.error("[factory-reset] failed", err);
                    setErrorSnackbar(
                      extractErrorMessage(
                        err,
                        "Factory reset failed — device did not respond",
                      ),
                    );
                    setCommandConfirmDialog(null);
                  },
                });
              } else if (commandConfirmDialog === "factory_reset") {
                setErrorSnackbar(
                  "Device is not online — cannot send factory reset.",
                );
                setCommandConfirmDialog(null);
              } else if (commandConfirmDialog) {
                handleSendCommand(commandConfirmDialog);
              }
            }}
            variant="contained"
            disabled={
              sendCommand.isPending ||
              factoryReset.isPending ||
              reboot.isPending
            }
            sx={{ bgcolor: "#FF6720", "&:hover": { bgcolor: "#F5490F" } }}
          >
            {sendCommand.isPending ||
            factoryReset.isPending ||
            reboot.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change-mode picker popup. Opened from the ⋮ menu; picking an env
          closes it and opens the type-to-confirm dialog below. Current env
          (VITE_APP_ENV) is the checked, non-selectable row. */}
      <Dialog
        open={modePickerOpen}
        onClose={() => setModePickerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ChangeModeIcon sx={{ color: "#FF6720" }} />
          Change device mode
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Switching environment disconnects the device from this CRM and
            reconnects it on the chosen backend. It may drop off this list until
            it checks in there.
          </Alert>
          <DialogContentText sx={{ mb: 1 }}>
            Current environment:{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {CURRENT_MODE?.label ?? "unknown"}
            </Box>
          </DialogContentText>
          <List disablePadding>
            {DEVICE_MODES.map((m) => {
              const isCurrent = m.key === CURRENT_MODE?.key;
              // The current row is a guarded no-op, NOT `disabled`: MUI stacks
              // the selected tint with 0.38 disabled opacity, which made the
              // text unreadable. Style it explicitly instead.
              const isDisabled =
                !isCurrent &&
                (!m.selectable ||
                  changeMode.isPending ||
                  displayStatus !== "online");
              return (
                <ListItemButton
                  key={m.key}
                  disabled={isDisabled}
                  disableRipple={isCurrent}
                  onClick={() => {
                    if (!isCurrent) handleSelectMode(m);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    border: "1px solid",
                    borderColor: isCurrent ? "#FF6720" : "#E0E0E0",
                    ...(isCurrent && {
                      bgcolor: "#FFF7F2",
                      cursor: "default",
                      "&:hover": { bgcolor: "#FFF7F2" },
                    }),
                    // Keep disabled rows legible (default 0.38 is too faint).
                    "&.Mui-disabled": { opacity: 0.55 },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isCurrent && (
                      <CheckIcon fontSize="small" sx={{ color: "#FF6720" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={m.label}
                    secondary={
                      isCurrent
                        ? undefined
                        : !m.selectable
                          ? "Not allowed"
                          : `persist.update.dev.mode = ${m.value}`
                    }
                    slotProps={{
                      primary: {
                        fontWeight: isCurrent ? 600 : 500,
                        color: "#161616",
                      },
                    }}
                  />
                  {isCurrent && (
                    <Chip
                      label="Current"
                      size="small"
                      sx={{
                        bgcolor: "#FF6720",
                        color: "#FFF",
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
          {displayStatus !== "online" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Device must be online to change mode.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModePickerOpen(false)}
            sx={{ color: "#161616" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!modeConfirm}
        onClose={() => setModeConfirm(null)}
        onConfirm={confirmChangeMode}
        title="Change device mode?"
        icon={<ChangeModeIcon sx={{ color: "#FF6720" }} />}
        description={
          modeConfirm
            ? `This will set persist.update.dev.mode=${modeConfirm.value} (${modeConfirm.label}). The device will disconnect from the current environment and reconnect on ${modeConfirm.label}. This action cannot be undone from here — type "confirm" to proceed.`
            : ""
        }
        requireTypedConfirmation="confirm"
        confirmLabel="OK"
        destructive
        loading={changeMode.isPending}
        loadingLabel="Sending…"
      />

      <Snackbar
        open={!!infoSnackbar}
        autoHideDuration={6000}
        onClose={() => setInfoSnackbar(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="info"
          onClose={() => setInfoSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {infoSnackbar}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={10000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Public entry point. Wraps the body in a DeviceStreamProvider so the
// page itself, the Live Logs / Activity / Memory tabs, and the
// admin-only Live Dashboard all share a single WebSocket per device
// instead of opening one each.
export function DeviceDetailPage() {
  const { id } = useParams<{ id: string; tab?: string }>();
  if (!id) return null;
  return (
    <DeviceStreamProvider deviceId={id} maxLogs={LIVE_LOG_BUFFER_CAP}>
      <DeviceDetailBody />
    </DeviceStreamProvider>
  );
}
